import React from "react";
import {
  AccountCircle,
  Assessment,
  Description,
  DirectionsCar,
  Email,
  ManageHistory,
  PublishedWithChanges,
  RequestQuote,
} from "@mui/icons-material";
import { IToggleTab } from "../../../components/Tabs/ToggleTabs.types";
import { isBusinessBinderVisible } from "./LeadBinderScopeContext";

/**
 * Lead Commercial-Auto scope descriptor supplied to the reused Commercial Auto
 * tab components when they are rendered inside the Manage Lead Comm Auto tab.
 * The presence of a lead scope re-keys those components' fetch/persist
 * operations from `customer_policy_id` to `lead_id`.
 *
 * Mirrors `ILeadBinderScope` from `LeadBinderScopeContext.tsx`.
 */
export interface ILeadCommAutoScope {
  leadId: string;
  customerId?: string | number | null;
}

/**
 * The context defaults to `null` so that `useLeadCommAutoScope` can be called
 * from a reused Commercial Auto component in either the customer-policy context
 * (no provider -> `null`) or the lead context (provider present).
 */
const LeadCommAutoScopeContext = React.createContext<ILeadCommAutoScope | null>(
  null,
);

/**
 * Provides the lead-scoped identifiers `{ leadId, customerId }` to descendant
 * Commercial Auto tab components rendered inside the Lead Comm Auto tab.
 */
export const LeadCommAutoScopeProvider: React.FC<{
  value: ILeadCommAutoScope;
  children: React.ReactNode;
}> = (props) => {
  return (
    <LeadCommAutoScopeContext.Provider value={props.value}>
      {props.children}
    </LeadCommAutoScopeContext.Provider>
  );
};

/**
 * Returns the current lead Commercial-Auto scope, or `null` when rendered
 * outside a lead Comm Auto container (e.g. in the customer-policy context).
 */
export const useLeadCommAutoScope = (): ILeadCommAutoScope | null =>
  React.useContext(LeadCommAutoScopeContext);

/**
 * Resolved scope returned by {@link useCommAutoScope}. `customerPolicyId` is
 * `undefined` in lead scope and `leadId` is `undefined` in customer-policy
 * scope, so a consumer can branch on which identifier is present.
 *
 * `customerId` / `customerPolicyId` are typed as `number | undefined` to match
 * how the reused Commercial Auto components consume them (they previously read
 * these from the loosely-typed router outlet tuple `[customerId,
 * customerPolicyId]`). In lead scope `customerPolicyId` is always `undefined`.
 */
export interface IResolvedCommAutoScope {
  customerId: number | undefined;
  customerPolicyId: number | undefined;
  leadId?: string;
}

/**
 * Compatibility resolver consumed by the reused Commercial Auto tab components
 * that previously read `[customerId, customerPolicyId]` from the router outlet.
 *
 * Unlike `useBinderScope`, this resolver does **not** call `useOutletContext()`
 * / `useParams()` internally (those throw outside a router `<Outlet>`, which is
 * the case inside the lead Comm Auto panel). Instead each consuming root keeps
 * its existing outlet/param read and passes the result in as `routeFallback`,
 * so this resolver stays a pure, trivially testable function of its inputs.
 *
 * - When a lead scope is present (non-empty `leadId`), it wins: the component
 *   receives `{ customerId, customerPolicyId: undefined, leadId }` so it
 *   re-keys by `lead_id` and omits `customer_policy_id`
 *   (Requirements 5.2, 5.4, 15.3).
 * - Otherwise it returns the passed `routeFallback` verbatim with
 *   `leadId: undefined`, preserving the original customer-policy behavior
 *   byte-for-byte (Requirements 5.3, 16.1).
 */
export function useCommAutoScope(routeFallback: {
  customerId?: number;
  customerPolicyId?: number;
}): IResolvedCommAutoScope {
  const leadScope = useLeadCommAutoScope();

  if (leadScope && isNonEmptyString(leadScope.leadId)) {
    return {
      customerId:
        leadScope.customerId == null
          ? undefined
          : Number(leadScope.customerId),
      customerPolicyId: undefined,
      leadId: leadScope.leadId,
    };
  }

  return {
    customerId: routeFallback?.customerId,
    customerPolicyId: routeFallback?.customerPolicyId,
    leadId: undefined,
  };
}

/**
 * Pure predicate that decides whether the Commercial Automobile tab should be
 * visible for a given lead `insurance_type` value.
 *
 * The comparison is exact and case-sensitive against the string
 * `"Commercial Automobile"`: no trimming of surrounding whitespace, no other
 * field is consulted. Any non-matching value (including `null`, `undefined`,
 * `""`, whitespace-only, `"commercial automobile"`, `" Commercial Automobile "`)
 * yields `false`. Mirrors `isBusinessBinderVisible` exactly.
 */
export function isCommAutoVisible(insuranceType: unknown): boolean {
  return insuranceType === "Commercial Automobile";
}

/**
 * The three mutually-exclusive Manage Lead tab-index-2 experiences.
 */
export type LeadExperience = "business" | "commAuto" | "default";

/**
 * Pure experience selector for the Manage Lead tab-index-2 slot. A lead carries
 * exactly one `insurance_type`, and `isBusinessBinderVisible` /
 * `isCommAutoVisible` match disjoint literals, so at most one specialized
 * experience applies. When neither predicate matches (including missing or
 * unreadable values) the selector returns `"default"` (Requirements 1.4, 1.5,
 * 16.5).
 */
export function selectLeadExperience(insuranceType: unknown): LeadExperience {
  if (isBusinessBinderVisible(insuranceType)) {
    return "business";
  }
  if (isCommAutoVisible(insuranceType)) {
    return "commAuto";
  }
  return "default";
}

/**
 * Pure tab-set builder for the Manage Lead top-level tab bar, extracted from
 * `ManageLeadTabs` so it is unit- and property-testable.
 *
 * Invariants (Requirements 2.1-2.4, 15.2, 16.5):
 * - Indices 0, 3, 4, 5 are always `"Lead"`, `"Activities"`, `"Email Thread"`,
 *   `"History"` in that order, unchanged across experiences.
 * - Insurance Reports (index 1) is present only for the `"default"` experience;
 *   hidden for `"business"` and `"commAuto"`.
 * - Index 2 label/icon: `"Risk(s)"` for business, `"Comm Auto"` for comm-auto,
 *   `"ARS Quote"` for default.
 *
 * The `"default"` branch reproduces the legacy default tab array byte-for-byte
 * (labels, icons, order) to guarantee non-regression.
 */
export function buildManageLeadTabs(
  experience: LeadExperience,
  _isUpdateLead?: boolean,
): IToggleTab[] {
  const isBusiness = experience === "business";
  const isCommAuto = experience === "commAuto";

  const indexTwoTab: IToggleTab = isBusiness
    ? { label: "Risk(s)", value: 2, icon: <Description /> }
    : isCommAuto
      ? { label: "Risk(s)", value: 2, icon: <DirectionsCar /> }
      : { label: "ARS Quote", value: 2, icon: <RequestQuote /> };

  return [
    { label: "Lead", value: 0, icon: <AccountCircle /> },
    // Insurance Reports (index 1) is only shown for the default experience.
    ...(isBusiness || isCommAuto
      ? []
      : [{ label: "Insurance Reports", value: 1, icon: <Assessment /> }]),
    indexTwoTab,
    { label: "Activities", value: 3, icon: <PublishedWithChanges /> },
    { label: "Email Thread", value: 4, icon: <Email /> },
    { label: "History", value: 5, icon: <ManageHistory /> },
  ];
}

/**
 * Pure decision for whether the lead Comm Auto container should mount at panel
 * index 2. It is `true` iff the resolved experience is `"commAuto"`, the lead
 * is saved (`isUpdateLead`), and a non-null `leadId` is present. In particular
 * it is `false` for any lead with a `null`/absent `leadId`, so no Comm Auto
 * component mounts and zero lead-scoped requests are issued for non-comm-auto
 * or unsaved leads (Requirements 2.1, 2.8, 16.6).
 */
export function shouldRenderCommAuto(
  experience: LeadExperience,
  isUpdateLead: boolean,
  leadId: unknown,
): boolean {
  return experience === "commAuto" && isUpdateLead === true && leadId != null;
}

/**
 * Internal helper: true only for a non-empty, non-whitespace string.
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
